import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { toast } from "sonner";
import { Shield, Palette, Layers, Save, RotateCcw, Download, Upload, KeyRound } from "lucide-react";
import { ACCENT_PALETTES, DEFAULT_CONFIG, checkPassword, saveConfig, resetConfig, setPassword } from "../lib/config";
import { USGS_FEEDS, REGION_PRESETS } from "../lib/api";

export default function AdminPanel({ open, onOpenChange, cfg, setCfg }) {
  const [authed, setAuthed] = useState(false);
  const [pw, setPw] = useState("");
  const [local, setLocal] = useState(cfg);
  const [newPw, setNewPw] = useState("");
  const [newPath, setNewPath] = useState(cfg.adminSecretPath);

  const tryAuth = () => {
    if (checkPassword(cfg, pw)) {
      setAuthed(true);
      setLocal(cfg);
      toast.success("Akses diberikan");
    } else {
      toast.error("Password salah");
    }
  };

  const save = () => {
    let next = { ...local };
    if (newPw) next = setPassword(next, newPw);
    next.adminSecretPath = newPath || "/secret-admin-pantau";
    saveConfig(next);
    setCfg(next);
    setNewPw("");
    toast.success("Konfigurasi tersimpan");
  };

  const reset = () => {
    resetConfig();
    setCfg({ ...DEFAULT_CONFIG });
    setLocal({ ...DEFAULT_CONFIG });
    toast.success("Konfigurasi direset ke default");
  };

  const exportCfg = () => {
    const blob = new Blob([JSON.stringify(local, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "pantau-config.json";
    a.click();
    toast.success("Konfigurasi diunduh");
  };

  const importCfg = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const r = new FileReader();
    r.onload = () => {
      try {
        const parsed = JSON.parse(r.result);
        setLocal({ ...DEFAULT_CONFIG, ...parsed });
        toast.success("Konfigurasi dimuat — klik Save untuk menerapkan");
      } catch { toast.error("File tidak valid"); }
    };
    r.readAsText(file);
  };

  const randomizePath = () => {
    const rnd = Math.random().toString(36).slice(2, 10);
    setNewPath(`/secret-${rnd}`);
    toast.info("Path admin diacak — jangan lupa Save");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="max-w-3xl max-h-[90vh] overflow-y-auto border-2 border-slate-700 bg-slate-900 text-slate-100 shadow-2xl shadow-cyan-500/10" data-testid="admin-panel">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-4 w-4 text-cyan-400" /> Secret Admin Panel
          </DialogTitle>
        </DialogHeader>

        {!authed ? (
          <div className="space-y-3 py-4">
            <Label>Master Password</Label>
            <Input
              type="password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && tryAuth()}
              className="border-slate-800 bg-slate-900"
              data-testid="admin-password-input"
            />
            <Button onClick={tryAuth} className="bg-cyan-500 text-slate-950 hover:bg-cyan-400" data-testid="admin-login-btn">
              <KeyRound className="mr-2 h-4 w-4" /> Unlock
            </Button>
          </div>
        ) : (
          <Tabs defaultValue="brand" className="w-full">
            <TabsList className="bg-slate-900">
              <TabsTrigger value="brand">Brand</TabsTrigger>
              <TabsTrigger value="theme">Theme</TabsTrigger>
              <TabsTrigger value="defaults">Defaults</TabsTrigger>
              <TabsTrigger value="security">Security</TabsTrigger>
              <TabsTrigger value="backup">Backup</TabsTrigger>
            </TabsList>

            <TabsContent value="brand" className="space-y-3 pt-4">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Site Name</Label><Input value={local.siteName} onChange={(e) => setLocal({ ...local, siteName: e.target.value })} className="border-slate-800 bg-slate-900" data-testid="cfg-siteName" /></div>
                <div><Label>Site Suffix</Label><Input value={local.siteSuffix} onChange={(e) => setLocal({ ...local, siteSuffix: e.target.value })} className="border-slate-800 bg-slate-900" /></div>
              </div>
              <div><Label>Subtitle</Label><Input value={local.siteSubtitle} onChange={(e) => setLocal({ ...local, siteSubtitle: e.target.value })} className="border-slate-800 bg-slate-900" /></div>
              <div><Label>Domain URL (watermark)</Label><Input placeholder="pantau.id" value={local.domainUrl} onChange={(e) => setLocal({ ...local, domainUrl: e.target.value })} className="border-slate-800 bg-slate-900" data-testid="cfg-domainUrl" /></div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Logo Type</Label>
                  <Select value={local.logoType} onValueChange={(v) => setLocal({ ...local, logoType: v })}>
                    <SelectTrigger className="border-slate-800 bg-slate-900" data-testid="cfg-logoType"><SelectValue /></SelectTrigger>
                    <SelectContent className="border-slate-800 bg-slate-900 text-slate-100">
                      <SelectItem value="monogram">Monogram</SelectItem>
                      <SelectItem value="icon">Icon (Lucide)</SelectItem>
                      <SelectItem value="custom_url">Custom URL</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Monogram Char</Label><Input maxLength={2} value={local.logoMonogramChar} onChange={(e) => setLocal({ ...local, logoMonogramChar: e.target.value })} className="border-slate-800 bg-slate-900" /></div>
                <div><Label>Lucide Icon Name</Label><Input placeholder="Radio, Globe, Shield…" value={local.logoIconName} onChange={(e) => setLocal({ ...local, logoIconName: e.target.value })} className="border-slate-800 bg-slate-900" /></div>
              </div>
              {local.logoType === "custom_url" && <div><Label>Custom Logo URL</Label><Input value={local.logoCustomUrl} onChange={(e) => setLocal({ ...local, logoCustomUrl: e.target.value })} className="border-slate-800 bg-slate-900" /></div>}
            </TabsContent>

            <TabsContent value="theme" className="space-y-3 pt-4">
              <Label className="flex items-center gap-1.5"><Palette className="h-3.5 w-3.5" /> Accent Color</Label>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {ACCENT_PALETTES.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => setLocal({ ...local, accentColor: p.id })}
                    className={`flex items-center gap-2 rounded-lg border p-3 text-left text-xs ${local.accentColor === p.id ? "border-cyan-400 bg-slate-900" : "border-slate-800 bg-slate-900/40 hover:bg-slate-900"}`}
                    data-testid={`cfg-accent-${p.id}`}
                  >
                    <span className="h-6 w-6 rounded-full ring-2 ring-slate-950" style={{ background: p.hex }}></span>
                    <span className="text-slate-200">{p.name}</span>
                  </button>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="defaults" className="space-y-3 pt-4">
              <div>
                <Label>Default Region</Label>
                <Select value={local.defaultRegion} onValueChange={(v) => setLocal({ ...local, defaultRegion: v })}>
                  <SelectTrigger className="border-slate-800 bg-slate-900"><SelectValue /></SelectTrigger>
                  <SelectContent className="border-slate-800 bg-slate-900 text-slate-100">
                    {Object.keys(REGION_PRESETS).map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Default Feed</Label>
                <Select value={local.defaultFeed} onValueChange={(v) => setLocal({ ...local, defaultFeed: v })}>
                  <SelectTrigger className="border-slate-800 bg-slate-900"><SelectValue /></SelectTrigger>
                  <SelectContent className="border-slate-800 bg-slate-900 text-slate-100">
                    <SelectItem value="combined">Combined</SelectItem>
                    <SelectItem value="bmkg">BMKG</SelectItem>
                    {USGS_FEEDS.map((f) => <SelectItem key={f.id} value={`usgs:${f.id}`}>USGS {f.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="flex items-center gap-1.5"><Layers className="h-3.5 w-3.5" /> Default Layers</Label>
                <div className="mt-2 space-y-2">
                  {Object.keys(local.defaultLayers).map((k) => (
                    <label key={k} className="flex items-center justify-between rounded-md bg-slate-900/60 px-3 py-2">
                      <span className="text-xs capitalize text-slate-200">{k}</span>
                      <Switch checked={local.defaultLayers[k]} onCheckedChange={(v) => setLocal({ ...local, defaultLayers: { ...local.defaultLayers, [k]: v } })} />
                    </label>
                  ))}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="security" className="space-y-3 pt-4">
              <div>
                <Label>Admin Secret Path</Label>
                <div className="flex gap-2">
                  <Input value={newPath} onChange={(e) => setNewPath(e.target.value)} className="border-slate-800 bg-slate-900" data-testid="cfg-adminPath" />
                  <Button variant="outline" onClick={randomizePath} className="border-slate-700 bg-slate-900 text-slate-100"><RotateCcw className="h-4 w-4" /></Button>
                </div>
              </div>
              <div>
                <Label>New Master Password (kosongkan bila tidak diubah)</Label>
                <Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} className="border-slate-800 bg-slate-900" data-testid="cfg-newPassword" />
              </div>
            </TabsContent>

            <TabsContent value="backup" className="space-y-3 pt-4">
              <div className="flex gap-2">
                <Button variant="outline" onClick={exportCfg} className="border-slate-700 bg-slate-900 text-slate-100" data-testid="cfg-export">
                  <Download className="mr-2 h-4 w-4" /> Export JSON
                </Button>
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100 hover:bg-slate-800">
                  <Upload className="h-4 w-4" /> Import JSON
                  <input type="file" accept="application/json" onChange={importCfg} className="hidden" data-testid="cfg-import" />
                </label>
                <Button variant="outline" onClick={reset} className="ml-auto border-red-500/40 bg-red-500/10 text-red-300 hover:bg-red-500/20" data-testid="cfg-reset">
                  <RotateCcw className="mr-2 h-4 w-4" /> Reset to Default
                </Button>
              </div>
            </TabsContent>

            <div className="flex justify-end gap-2 border-t border-slate-800 pt-3">
              <Button onClick={save} className="bg-cyan-500 text-slate-950 hover:bg-cyan-400" data-testid="admin-save-btn">
                <Save className="mr-2 h-4 w-4" /> Save
              </Button>
            </div>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}
